import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Svg,
  Path,
  Polygon,
} from '@react-pdf/renderer';

const COLORS = {
  blue: '#064aa2',
  blueDark: '#003b88',
  blueDeep: '#00347a',
  blueLine: '#89a9d8',
  blueLight: '#eef7ff',
  text: '#111827',
  muted: '#4b5563',
  green: '#0b8a2a',
  greenLine: '#55a86b',
  purple: '#6c42aa',
  purpleLine: '#9f82c9',
  red: '#d60000',
};

const COLUMN_WIDTHS = {
  subject: '25.5%',
  sba: '12%',
  exams: '12%',
  total: '12%',
  grade: '11.5%',
  rank: '9.5%',
  remarks: '17.5%',
};

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: COLORS.text,
    backgroundColor: '#ffffff',
    position: 'relative',
  },
  topRule: {
    height: 6,
    backgroundColor: COLORS.blueDeep,
  },
  header: {
    height: 136,
    backgroundColor: '#eff8ff',
    position: 'relative',
    overflow: 'hidden',
    borderBottomWidth: 2,
    borderBottomColor: COLORS.blue,
  },
  headerArt: {
    position: 'absolute',
    left: 0,
    top: 0,
  },
  headerContent: {
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
    height: '100%',
    paddingHorizontal: 22,
    paddingTop: 10,
    paddingBottom: 10,
  },
  logoWrap: {
    width: 78,
    height: 88,
    alignItems: 'center',
    justifyContent: 'center',
  },
  schoolLogo: {
    width: 76,
    height: 78,
    objectFit: 'contain',
  },
  schoolLogoFallback: {
    width: 72,
    height: 72,
    borderRadius: 36,
    borderWidth: 1.5,
    borderColor: COLORS.blue,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8fbff',
  },
  schoolLogoFallbackText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: COLORS.blue,
  },
  headerCenter: {
    flex: 1,
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  schoolName: {
    fontSize: 17,
    lineHeight: 1.1,
    fontWeight: 'bold',
    color: COLORS.blueDark,
    textAlign: 'center',
    textTransform: 'uppercase',
    marginBottom: 15,
  },
  contactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 7,
  },
  singleContactRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: 285,
    marginBottom: 7,
  },
  contactInline: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginHorizontal: 10,
  },
  contactText: {
    fontSize: 11.5,
    color: '#0f1b3b',
    marginLeft: 6,
  },
  contactDivider: {
    fontSize: 11,
    color: COLORS.blue,
    marginHorizontal: 2,
  },
  mottoText: {
    fontSize: 11.5,
    color: '#0f1b3b',
    fontStyle: 'italic',
    marginLeft: 6,
  },
  photoWrap: {
    width: 72,
    height: 92,
    borderRadius: 8,
    padding: 0,
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  },
  studentPicture: {
    width: 72,
    height: 92,
    borderRadius: 6,
    objectFit: 'cover',
  },
  studentPictureFallback: {
    width: 72,
    height: 92,
    borderRadius: 6,
    backgroundColor: '#dbeafe',
    alignItems: 'center',
    justifyContent: 'center',
  },
  studentPictureFallbackText: {
    fontSize: 16,
    color: COLORS.blue,
    fontWeight: 'bold',
  },
  content: {
    paddingHorizontal: 34,
    paddingTop: 15,
    paddingBottom: 32,
  },
  titleWrap: {
    alignItems: 'center',
    marginBottom: 12,
  },
  reportTitle: {
    backgroundColor: COLORS.blue,
    color: '#ffffff',
    fontSize: 15,
    fontWeight: 'bold',
    textAlign: 'center',
    paddingVertical: 6,
    paddingHorizontal: 18,
    borderRadius: 6,
    minWidth: 280,
  },
  studentInfoPanel: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: COLORS.blue,
    borderRadius: 7,
    backgroundColor: '#f8fbff',
    paddingVertical: 10,
    paddingHorizontal: 15,
    marginBottom: 10,
  },
  infoColumn: {
    flex: 1,
  },
  infoDivider: {
    width: 1,
    backgroundColor: '#a9bdd9',
    marginHorizontal: 19,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 9,
  },
  infoRowLast: {
    marginBottom: 0,
  },
  infoLabel: {
    width: 95,
    fontSize: 10,
    color: COLORS.blue,
    fontWeight: 'bold',
  },
  infoLabelRight: {
    width: 98,
    fontSize: 10,
    color: COLORS.blue,
    fontWeight: 'bold',
  },
  infoValue: {
    flex: 1,
    fontSize: 10,
    color: COLORS.text,
  },
  table: {
    width: '100%',
    borderWidth: 1,
    borderColor: COLORS.blueLine,
    borderRadius: 6,
    overflow: 'hidden',
  },
  tableRow: {
    flexDirection: 'row',
    width: '100%',
  },
  tableHeader: {
    backgroundColor: COLORS.blue,
  },
  tableHeaderCell: {
    minHeight: 39,
    borderRightWidth: 1,
    borderRightColor: '#99b9e8',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  tableHeaderCellLast: {
    borderRightWidth: 0,
  },
  tableHeaderText: {
    color: '#ffffff',
    fontSize: 9,
    lineHeight: 1.05,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  tableCell: {
    minHeight: 20,
    borderRightWidth: 1,
    borderRightColor: COLORS.blueLine,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.blueLine,
    paddingHorizontal: 5,
    paddingVertical: 3.5,
    justifyContent: 'center',
  },
  tableCellLast: {
    borderRightWidth: 0,
  },
  tableText: {
    fontSize: 8.8,
    color: COLORS.text,
    textAlign: 'center',
  },
  subjectText: {
    fontSize: 8.8,
    color: COLORS.text,
    textAlign: 'left',
    lineHeight: 1.05,
  },
  totalText: {
    fontWeight: 'bold',
  },
  gradeText: {
    fontWeight: 'bold',
  },
  attendanceCell: {
    minHeight: 22,
    borderRightWidth: 1,
    borderRightColor: COLORS.blueLine,
    paddingHorizontal: 5,
    paddingVertical: 5,
    justifyContent: 'center',
    backgroundColor: '#eff6ff',
  },
  attendanceCellLast: {
    borderRightWidth: 0,
  },
  attendanceText: {
    fontSize: 9.5,
    color: COLORS.blueDark,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  attendanceTextLeft: {
    textAlign: 'center',
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 11,
  },
  conductCard: {
    width: '49%',
    height: 156,
    borderWidth: 1,
    borderColor: COLORS.greenLine,
    borderRadius: 6,
    paddingTop: 10,
    paddingHorizontal: 11,
    paddingBottom: 10,
    backgroundColor: '#fbfff9',
  },
  rightSummaryColumn: {
    width: '49%',
  },
  performanceCard: {
    height: 75,
    borderWidth: 1,
    borderColor: COLORS.blueLine,
    borderRadius: 6,
    paddingTop: 9,
    paddingHorizontal: 11,
    paddingBottom: 9,
    backgroundColor: '#fbfdff',
    marginBottom: 7,
  },
  gradingCard: {
    height: 78,
    borderWidth: 1,
    borderColor: COLORS.purpleLine,
    borderRadius: 6,
    paddingTop: 9,
    paddingHorizontal: 11,
    paddingBottom: 8,
    backgroundColor: '#fefcff',
  },
  cardTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 7,
  },
  cardTitleText: {
    fontSize: 9.5,
    fontWeight: 'bold',
  },
  cardRule: {
    height: 1,
    marginLeft: 0,
    marginTop: -5,
    marginBottom: 7,
  },
  formLineRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 9,
  },
  formLabel: {
    width: 52,
    fontSize: 9.2,
    color: COLORS.green,
    fontWeight: 'bold',
  },
  formValueWrap: {
    flex: 1,
    borderBottomWidth: 1,
    borderBottomColor: '#8bc79b',
    paddingBottom: 3,
  },
  formValue: {
    fontSize: 9.4,
    lineHeight: 1.3,
    color: COLORS.text,
  },
  teacherRemarksLabel: {
    marginTop: 12,
    marginBottom: 8,
    fontSize: 9.4,
    color: COLORS.green,
    fontWeight: 'bold',
  },
  teacherRemarksText: {
    fontSize: 9.6,
    fontStyle: 'italic',
    color: COLORS.text,
    lineHeight: 1.35,
  },
  summaryMetricRow: {
    flexDirection: 'row',
    marginBottom: 3,
  },
  summaryMetricLabel: {
    width: 108,
    fontSize: 8.4,
    color: COLORS.blueDark,
    fontWeight: 'bold',
  },
  summaryMetricValue: {
    flex: 1,
    fontSize: 8.4,
    color: COLORS.text,
  },
  gradingLine: {
    fontSize: 7.3,
    color: COLORS.text,
    lineHeight: 1.18,
  },
  signatures: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingHorizontal: 50,
    marginTop: 9,
  },
  signatureBlock: {
    width: 150,
    alignItems: 'center',
  },
  signatureImage: {
    width: 112,
    height: 34,
    objectFit: 'contain',
    marginBottom: 2,
  },
  signatureLine: {
    width: 126,
    borderTopWidth: 1,
    borderTopColor: '#111827',
    marginTop: 22,
    marginBottom: 6,
  },
  signatureLineWithImage: {
    marginTop: 0,
  },
  signatureText: {
    fontSize: 8,
    fontWeight: 'bold',
    color: COLORS.text,
    textAlign: 'center',
  },
  footer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 31,
    backgroundColor: COLORS.blue,
    borderTopWidth: 2,
    borderTopColor: COLORS.blueDeep,
    alignItems: 'center',
    justifyContent: 'center',
  },
  footerText: {
    color: '#ffffff',
    fontSize: 8.2,
    textAlign: 'center',
  },
});

const getInitials = (name) => {
  if (!name) return 'S';
  return String(name)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
};

const formatRank = (rank) => {
  if (!rank) return '-';
  const numericRank = Number(rank);
  if (!Number.isFinite(numericRank)) return rank;
  const j = numericRank % 10;
  const k = numericRank % 100;
  if (j === 1 && k !== 11) return `${numericRank}st`;
  if (j === 2 && k !== 12) return `${numericRank}nd`;
  if (j === 3 && k !== 13) return `${numericRank}rd`;
  return `${numericRank}th`;
};

const displayValue = (value, fallback = '-') => {
  if (value === undefined || value === null || value === '') return fallback;
  return value;
};

const displayWholeNumber = (value, fallback = '-') => {
  if (value === undefined || value === null || value === '') return fallback;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return value;
  return Math.round(numericValue);
};

const normalizeClassLabel = (value) => (
  String(value || '')
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim()
);

const displayPromotedTo = (value, className) => {
  const normalizedValue = normalizeClassLabel(value);
  if (!normalizedValue) return '';
  if (normalizedValue === 'REPEATED') return 'REPEATED';
  if (normalizeClassLabel(className) === normalizedValue) return 'REPEATED';
  return value;
};

const HeaderBackground = () => (
  <Svg width="595" height="136" style={styles.headerArt}>
    <Polygon points="0,0 595,0 595,136 0,136" fill="#f3fbff" />
    <Polygon points="0,42 76,136 0,136" fill="#d9efff" opacity="0.75" />
    <Polygon points="497,0 595,0 595,136 438,136" fill="#d8efff" opacity="0.85" />
    <Polygon points="544,0 595,0 595,72" fill="#c7e8ff" opacity="0.85" />
    <Polygon points="96,136 110,130 487,130 499,136" fill={COLORS.blue} />
  </Svg>
);

const ContactIcon = ({ type }) => {
  if (type === 'email') {
    return (
      <Svg width="12" height="12" viewBox="0 0 24 24">
        <Path d="M3 6h18v12H3z" fill={COLORS.blueDark} />
        <Path d="M3 7l9 7 9-7" stroke="#ffffff" strokeWidth="1.8" fill="none" />
      </Svg>
    );
  }

  if (type === 'phone') {
    return (
      <Svg width="12" height="12" viewBox="0 0 24 24">
        <Path
          d="M6.6 3.5l3.1 1.2-1.5 4c1.2 2.5 3.1 4.4 5.8 5.8l3.9-1.6 1.3 3.2c.3.7.1 1.5-.6 1.9-1.1.7-2.3 1-3.5.7C10 17.6 6.1 13.9 4.6 8.8 4.1 7.1 4.5 5.4 5.5 4c.3-.5.7-.6 1.1-.5z"
          fill={COLORS.blueDark}
        />
      </Svg>
    );
  }

  if (type === 'motto') {
    return (
      <Svg width="12" height="12" viewBox="0 0 24 24">
        <Path
          d="M4 4.5h10.5c2.5 0 4.5 2 4.5 4.5v11.5H7.5C5.6 20.5 4 18.9 4 17V4.5zm3 3v10h10V9c0-.8-.7-1.5-1.5-1.5H7zm2.2 3h5.6v1.6H9.2v-1.6zm0 3.2h4.4v1.6H9.2v-1.6z"
          fill={COLORS.blueDark}
        />
      </Svg>
    );
  }

  return (
    <Svg width="12" height="12" viewBox="0 0 24 24">
      <Path
        d="M12 2.5c-3.4 0-6.2 2.7-6.2 6.1 0 4.5 6.2 12.9 6.2 12.9s6.2-8.4 6.2-12.9c0-3.4-2.8-6.1-6.2-6.1zm0 8.5c-1.3 0-2.3-1-2.3-2.3s1-2.3 2.3-2.3 2.3 1 2.3 2.3S13.3 11 12 11z"
        fill={COLORS.blueDark}
      />
    </Svg>
  );
};

const getGradeColor = (grade, rawGrade) => {
  const gradeText = String(grade || rawGrade || '').toUpperCase();

  if (gradeText === 'A' || gradeText.includes('ADVANCE') || gradeText.includes('GOLD')) return COLORS.blue;
  if (gradeText === 'P' || gradeText.includes('PROFICIENT') || gradeText.includes('SILVER')) return COLORS.green;
  if (gradeText === 'AP') return '#0b64b7';
  if (gradeText === 'D' || gradeText.includes('DEVELOPING')) return '#b7791f';
  if (gradeText === 'B' || gradeText === 'F' || gradeText.includes('BEGINNING') || gradeText.includes('BRONZE')) return COLORS.red;

  return COLORS.text;
};

const StudentReportCardPage = ({ studentReport, schoolInfo, className, termName, academicYear, noOnRoll, nextTermBegins, reportDate }) => {
  const { studentInfo, subjects, totalMarks, averageMarks, position } = studentReport;
  const pageNextTermBegins = studentInfo?.nextTermBegins || nextTermBegins;

  const isNursery = className && (
    className.toUpperCase().includes('NURSERY') ||
    className.toUpperCase().includes('KG') ||
    className.toUpperCase().includes('CRECHE')
  );

  const getOverallGrade = (total) => {
    if (isNursery) {
      if (total >= 70) return 'Gold (G)';
      if (total >= 40) return 'Silver (S)';
      return 'Bronze (B)';
    }
    if (total >= 80) return 'Advance (A)';
    if (total >= 75) return 'Proficient (P)';
    if (total >= 70) return 'Approaching Proficiency (AP)';
    if (total >= 65) return 'Developing (D)';
    return 'Beginning (B)';
  };

  const getRemark = (grade) => {
    if (!grade) return '';

    if (grade.includes('(') && grade.includes(')')) {
      return grade.split(' (')[0];
    }

    if (isNursery) {
      switch (grade) {
        case 'G': return 'Gold';
        case 'S': return 'Silver';
        case 'B': return 'Bronze';
        default: return grade;
      }
    }

    switch (grade) {
      case 'A': return 'Advance';
      case 'P': return 'Proficient';
      case 'AP': return 'Approaching Proficiency';
      case 'D': return 'Developing';
      case 'B': return 'Beginning';
      default: return grade;
    }
  };

  const getShortGrade = (grade) => {
    if (!grade) return '-';
    const remark = getRemark(grade);
    switch (remark) {
      case 'Gold': return 'G';
      case 'Silver': return 'S';
      case 'Bronze': return 'B';
      case 'Advance': return 'A';
      case 'Proficient': return 'P';
      case 'Approaching Proficiency': return 'AP';
      case 'Developing': return 'D';
      case 'Beginning': return 'B';
      default:
        if (grade.includes('(') && grade.includes(')')) {
          const match = grade.match(/\(([^)]+)\)/);
          if (match) return match[1];
        }
        return grade;
    }
  };

  const overallGradeRaw = getOverallGrade(averageMarks);
  const overallGradeShort = getShortGrade(overallGradeRaw);
  const overallGradeRemark = getRemark(overallGradeRaw);
  const vacationDate = reportDate || 'TBD';
  const generatedDate = new Date().toLocaleDateString('en-GB');
  const adminName = schoolInfo?.adminName || schoolInfo?.headTeacherName || schoolInfo?.headteacher || schoolInfo?.headTeacher || 'Admin';
  const adminSignature = schoolInfo?.adminSignature || schoolInfo?.headTeacherSignature || schoolInfo?.headteacherSignature;
  const gradingLines = isNursery
    ? ['70-100: Gold (G)', '40-69: Silver (S)', '0-39: Bronze (B)']
    : [
      '80-100: Advance (A)',
      '75-79: Proficient (P)',
      '70-74: Approaching Proficiency (AP)',
      '65-69: Developing (D)',
      '0-64: Beginning (B)',
    ];

  return (
    <Page size="A4" style={styles.page}>
      <View style={styles.topRule} />

      <View style={styles.header}>
        <HeaderBackground />
        <View style={styles.headerContent}>
          <View style={styles.logoWrap}>
            {schoolInfo?.logo ? (
              <Image style={styles.schoolLogo} src={schoolInfo.logo} />
            ) : (
              <View style={styles.schoolLogoFallback}>
                <Text style={styles.schoolLogoFallbackText}>{getInitials(schoolInfo?.name || 'School')}</Text>
              </View>
            )}
          </View>

          <View style={styles.headerCenter}>
            <Text style={styles.schoolName}>{schoolInfo?.name || 'School Name'}</Text>

            <View style={styles.singleContactRow}>
              <ContactIcon type="location" />
              <Text style={styles.contactText}>{schoolInfo?.address || 'School Address'}</Text>
            </View>

            <View style={styles.contactRow}>
              <View style={styles.contactInline}>
                <ContactIcon type="email" />
                <Text style={styles.contactText}>{schoolInfo?.email || 'email@school.com'}</Text>
              </View>
              <Text style={styles.contactDivider}>|</Text>
              <View style={styles.contactInline}>
                <ContactIcon type="phone" />
                <Text style={styles.contactText}>{schoolInfo?.phone || '+123456789'}</Text>
              </View>
            </View>

            {schoolInfo?.motto ? (
              <View style={styles.singleContactRow}>
                <ContactIcon type="motto" />
                <Text style={styles.mottoText}>Motto: {schoolInfo.motto}</Text>
              </View>
            ) : null}
          </View>

          <View style={styles.photoWrap}>
            {studentInfo?.profilePicture ? (
              <Image style={styles.studentPicture} src={studentInfo.profilePicture} />
            ) : (
              <View style={styles.studentPictureFallback}>
                <Text style={styles.studentPictureFallbackText}>{getInitials(studentInfo?.name || 'Student')}</Text>
              </View>
            )}
          </View>
        </View>
      </View>

      <View style={styles.content}>
        <View style={styles.titleWrap}>
          <Text style={styles.reportTitle}>STUDENT TERMINAL REPORT CARD</Text>
        </View>

        <View style={styles.studentInfoPanel}>
          <View style={styles.infoColumn}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Name:</Text>
              <Text style={styles.infoValue}>{displayValue(studentInfo?.name)}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Class:</Text>
              <Text style={styles.infoValue}>{displayValue(className)}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Position:</Text>
              <Text style={styles.infoValue}>{displayValue(position)}</Text>
            </View>
            <View style={[styles.infoRow, styles.infoRowLast]}>
              <Text style={styles.infoLabel}>No on Roll:</Text>
              <Text style={styles.infoValue}>{displayValue(noOnRoll)}</Text>
            </View>
          </View>

          <View style={styles.infoDivider} />

          <View style={styles.infoColumn}>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabelRight}>Term:</Text>
              <Text style={styles.infoValue}>{displayValue(termName)} - {displayValue(academicYear)}</Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabelRight}>Next Term Begins:</Text>
              <Text style={styles.infoValue}>{displayValue(pageNextTermBegins)}</Text>
            </View>
            <View style={[styles.infoRow, styles.infoRowLast]}>
              <Text style={styles.infoLabelRight}>Vacation Date:</Text>
              <Text style={styles.infoValue}>{vacationDate}</Text>
            </View>
          </View>
        </View>

        <View style={styles.table}>
          <View style={[styles.tableRow, styles.tableHeader]}>
            <View style={[styles.tableHeaderCell, { width: COLUMN_WIDTHS.subject }]}>
              <Text style={styles.tableHeaderText}>SUBJECT</Text>
            </View>
            <View style={[styles.tableHeaderCell, { width: COLUMN_WIDTHS.sba }]}>
              <Text style={styles.tableHeaderText}>SBA{'\n'}SCORE{'\n'}50%</Text>
            </View>
            <View style={[styles.tableHeaderCell, { width: COLUMN_WIDTHS.exams }]}>
              <Text style={styles.tableHeaderText}>EXAMS{'\n'}SCORE{'\n'}50%</Text>
            </View>
            <View style={[styles.tableHeaderCell, { width: COLUMN_WIDTHS.total }]}>
              <Text style={styles.tableHeaderText}>TOTAL{'\n'}SCORE{'\n'}100%</Text>
            </View>
            <View style={[styles.tableHeaderCell, { width: COLUMN_WIDTHS.grade }]}>
              <Text style={styles.tableHeaderText}>GRADE</Text>
            </View>
            <View style={[styles.tableHeaderCell, { width: COLUMN_WIDTHS.rank }]}>
              <Text style={styles.tableHeaderText}>RANK</Text>
            </View>
            <View style={[styles.tableHeaderCell, styles.tableHeaderCellLast, { width: COLUMN_WIDTHS.remarks }]}>
              <Text style={styles.tableHeaderText}>REMARKS</Text>
            </View>
          </View>

          {subjects.map((sub, index) => {
            const shortGrade = getShortGrade(sub.grade);
            const remark = getRemark(sub.grade).toUpperCase();
            const rowBackground = index % 2 === 0 ? '#ffffff' : '#f8fbff';

            return (
              <View style={[styles.tableRow, { backgroundColor: rowBackground }]} key={index}>
                <View style={[styles.tableCell, { width: COLUMN_WIDTHS.subject }]}>
                  <Text style={styles.subjectText}>{sub.subject.name}</Text>
                </View>
                <View style={[styles.tableCell, { width: COLUMN_WIDTHS.sba }]}>
                  <Text style={styles.tableText}>{displayWholeNumber(sub.scaledSba)}</Text>
                </View>
                <View style={[styles.tableCell, { width: COLUMN_WIDTHS.exams }]}>
                  <Text style={styles.tableText}>{displayWholeNumber(sub.scaledExams)}</Text>
                </View>
                <View style={[styles.tableCell, { width: COLUMN_WIDTHS.total }]}>
                  <Text style={[styles.tableText, styles.totalText]}>{displayWholeNumber(sub.total)}</Text>
                </View>
                <View style={[styles.tableCell, { width: COLUMN_WIDTHS.grade }]}>
                  <Text style={[styles.tableText, styles.gradeText, { color: getGradeColor(shortGrade, sub.grade) }]}>{shortGrade}</Text>
                </View>
                <View style={[styles.tableCell, { width: COLUMN_WIDTHS.rank }]}>
                  <Text style={styles.tableText}>{formatRank(sub.subjectRank)}</Text>
                </View>
                <View style={[styles.tableCell, styles.tableCellLast, { width: COLUMN_WIDTHS.remarks }]}>
                  <Text style={styles.tableText}>{remark || '-'}</Text>
                </View>
              </View>
            );
          })}

          <View style={styles.tableRow}>
            <View style={[styles.attendanceCell, { width: COLUMN_WIDTHS.subject }]}>
              <Text style={[styles.attendanceText, styles.attendanceTextLeft]}>ATTENDANCE:</Text>
            </View>
            <View style={[styles.attendanceCell, { width: COLUMN_WIDTHS.sba }]}>
              <Text style={styles.attendanceText}>{studentInfo?.attendance ?? ''}</Text>
            </View>
            <View style={[styles.attendanceCell, { width: COLUMN_WIDTHS.exams }]}>
              <Text style={styles.attendanceText}>OUT OF</Text>
            </View>
            <View style={[styles.attendanceCell, { width: COLUMN_WIDTHS.total }]}>
              <Text style={styles.attendanceText}>{studentInfo?.timesOpen ?? 0}</Text>
            </View>
            <View style={[styles.attendanceCell, { width: '21%' }]}>
              <Text style={styles.attendanceText}>PROMOTED TO</Text>
            </View>
            <View style={[styles.attendanceCell, styles.attendanceCellLast, { width: '17.5%' }]}>
              <Text style={styles.attendanceText}>{displayPromotedTo(studentInfo?.promotedTo, className)}</Text>
            </View>
          </View>
        </View>

        <View style={styles.summaryRow}>
          <View style={styles.conductCard}>
            <View style={styles.cardTitleRow}>
              <Text style={[styles.cardTitleText, { color: COLORS.green }]}>Conduct &amp; Interest</Text>
            </View>
            <View style={[styles.cardRule, { backgroundColor: COLORS.greenLine }]} />

            <View style={styles.formLineRow}>
              <Text style={styles.formLabel}>Conduct:</Text>
              <View style={styles.formValueWrap}>
                <Text style={styles.formValue}>{studentInfo?.conduct || ''}</Text>
              </View>
            </View>

            <View style={styles.formLineRow}>
              <Text style={styles.formLabel}>Interest:</Text>
              <View style={styles.formValueWrap}>
                <Text style={styles.formValue}>{studentInfo?.interest || ''}</Text>
              </View>
            </View>

            <Text style={styles.teacherRemarksLabel}>Class Teacher's Remarks</Text>
            <Text style={styles.teacherRemarksText}>
              {studentInfo?.teacherRemarks || (
                averageMarks >= 80 ? 'An excellent performance this term. Keep up the good work!' :
                  averageMarks >= 60 ? 'Good effort, but there is room for improvement next term.' :
                    'Needs to put in more effort to improve overall performance.'
              )}
            </Text>
          </View>

          <View style={styles.rightSummaryColumn}>
            <View style={styles.performanceCard}>
              <View style={styles.cardTitleRow}>
                <Text style={[styles.cardTitleText, { color: COLORS.blueDark }]}>Performance Summary</Text>
              </View>
              <View style={[styles.cardRule, { backgroundColor: COLORS.blueLine }]} />

              <View style={styles.summaryMetricRow}>
                <Text style={styles.summaryMetricLabel}>Total Score:</Text>
                <Text style={styles.summaryMetricValue}>{displayWholeNumber(totalMarks)}</Text>
              </View>
              <View style={styles.summaryMetricRow}>
                <Text style={styles.summaryMetricLabel}>Average:</Text>
                <Text style={styles.summaryMetricValue}>
                  {averageMarks === undefined || averageMarks === null || averageMarks === '' ? '-' : `${displayWholeNumber(averageMarks)}%`}
                </Text>
              </View>
              <View style={styles.summaryMetricRow}>
                <Text style={styles.summaryMetricLabel}>Overall Grade:</Text>
                <Text style={styles.summaryMetricValue}>{overallGradeShort} ({overallGradeRemark})</Text>
              </View>
            </View>

            <View style={styles.gradingCard}>
              <View style={styles.cardTitleRow}>
                <Text style={[styles.cardTitleText, { color: COLORS.purple }]}>Grading System</Text>
              </View>
              <View style={[styles.cardRule, { backgroundColor: COLORS.purpleLine }]} />

              {gradingLines.map((line) => (
                <Text style={styles.gradingLine} key={line}>{line}</Text>
              ))}
            </View>
          </View>
        </View>

        <View style={styles.signatures}>
          <View style={styles.signatureBlock}>
            {adminSignature ? (
              <Image style={styles.signatureImage} src={adminSignature} />
            ) : null}
            <View style={[styles.signatureLine, adminSignature ? styles.signatureLineWithImage : null]} />
            <Text style={styles.signatureText}>{adminName}</Text>
          </View>
        </View>
      </View>

      <View style={styles.footer}>
        <Text style={styles.footerText}>
          Generated on {generatedDate}  |  This report card is a valid document of the school.
        </Text>
      </View>
    </Page>
  );
};

export const SingleStudentReportCardPDF = (props) => (
  <Document>
    <StudentReportCardPage {...props} />
  </Document>
);

export const ClassReportCardsDocument = ({ reportCards, schoolInfo, className, termName, academicYear, noOnRoll, nextTermBegins, reportDate }) => (
  <Document>
    {reportCards.map(report => (
      <StudentReportCardPage
        key={report.studentInfo.id}
        studentReport={report}
        schoolInfo={schoolInfo}
        className={className}
        termName={termName}
        academicYear={academicYear}
        noOnRoll={noOnRoll}
        nextTermBegins={nextTermBegins}
        reportDate={reportDate}
      />
    ))}
  </Document>
);
